/**
 * Returns `next` only when it's a safe *same-origin* redirect target, otherwise `fallback`.
 * Guards against open redirects — an attacker-supplied `?next=https://evil.com` (or the
 * protocol-relative `//evil.com`) must never send the user off-site after auth.
 */
export const getSafeRedirectUrl = (
  next: string | string[] | undefined | null,
  fallback: string,
): string => {
  const value = Array.isArray(next) ? next[0] : next;
  if (!value) {
    return fallback;
  }

  // No `window` to compare origins against (SSR) — can't validate, so use the safe fallback.
  if (typeof window === 'undefined') {
    return fallback;
  }

  try {
    const decoded = decodeURIComponent(value);
    const resolved = new URL(decoded, window.location.origin);
    return resolved.origin === window.location.origin ? decoded : fallback;
  } catch {
    return fallback;
  }
};

/**
 * Server-safe variant: returns `value` only when it's a same-origin *path* (`/foo?bar`),
 * otherwise undefined. There is no `window` on the server to compare origins against, so
 * absolute and protocol-relative URLs are rejected outright rather than resolved.
 */
export const getSafeRedirectPath = (value: unknown): string | undefined => {
  if (typeof value !== 'string' || !value.startsWith('/')) {
    return undefined;
  }
  // `//evil.com` and `/\evil.com` are protocol-relative and leave the origin.
  if (value.startsWith('//') || value.startsWith('/\\')) {
    return undefined;
  }
  return value;
};
